import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { type Schema as PMSchema } from '@atlaskit/editor-prosemirror/model';
import { ACTION } from '@atlaskit/editor-common/analytics';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';

import type { EditorOfflineStoragePlugin } from '../editorOfflineStoragePluginType';
import type { StepsDatabase } from '../stepsDatabase';
import { fireOfflineStorageAnalyticsEvent } from '../analytics';

import { applyStepsCommand, noStepsCommand, retryCommand, tooManyRetriesCommand } from './commands';
const MAX_RETRIES = 1;

function handleRetry(
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined,
	retrySource: string,
	error?: unknown,
) {
	const sharedPluginState = api?.editorOfflineStorage?.sharedState.currentState();
	const retryCount = sharedPluginState?.retryCount ?? 0;
	if (retryCount >= MAX_RETRIES) {
		api?.core.actions.execute(tooManyRetriesCommand());

		const parseErrorMessage = () => {
			if (!error) {
				return 'Unknown error';
			}
			if (error instanceof Error) {
				return error.message;
			}
			return String(error);
		};

		fireOfflineStorageAnalyticsEvent({
			api,
			action: ACTION.OFFLINE_STORAGE_TOO_MANY_RETRIES_ERROR,
			errorMessage: `Exceeded max retries (${MAX_RETRIES}) via ${retrySource}`,
			errorInfo: parseErrorMessage(),
			retrySource,
			maxRetries: MAX_RETRIES,
		});
	} else {
		api?.core.actions.execute(retryCommand(retryCount));
	}
}

export function restoreStepsAction(
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined,
	stepsDatabase: StepsDatabase,
) {
	return async (pmSchema: PMSchema) => {
		try {
			const databases = await window.indexedDB.databases();
			const dbExists = Boolean(databases.find((db) => db.name === stepsDatabase.dbName));

			if (!dbExists) {
				api?.core.actions.execute(noStepsCommand());
				return;
			}

			const { steps } = await stepsDatabase.getData(pmSchema);
			const dispatched = api?.core.actions.execute(applyStepsCommand(steps));
			if (!dispatched) {
				handleRetry(api, 'stepsNotDispatched');
				return;
			}

			try {
				await stepsDatabase.clear();
			} catch (e) {
				// steps were applied but failed to clear, continue
			}
		} catch (error) {
			handleRetry(api, 'restoreStepsError', error);
		}
	};
}
